import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

function getTodayCT() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}

export default function DriverMaintenanceSubmitPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [trucks, setTrucks] = useState([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Form state
  const [truckId, setTruckId] = useState('');
  const [maintenanceTypeId, setMaintenanceTypeId] = useState('');
  const [dateCompleted, setDateCompleted] = useState(getTodayCT());
  const [odometer, setOdometer] = useState('');
  const [vendorOrShop, setVendorOrShop] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [receiptPhoto, setReceiptPhoto] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [trucksData, typesData] = await Promise.all([
          api.getTrucks(),
          api.getMaintenanceTypes(),
        ]);

        const truckList = Array.isArray(trucksData) ? trucksData : (trucksData.trucks || []);
        setTrucks(truckList);

        // Pre-select assigned truck
        if (user && truckList.length > 0) {
          const assigned = truckList.find(t => t.assigned_driver_id === user.id);
          if (assigned) setTruckId(String(assigned.id));
        }

        const typeList = Array.isArray(typesData) ? typesData : (typesData.types || typesData.maintenanceTypes || []);
        setMaintenanceTypes(typeList);
      } catch (err) {
        setError(err.message || 'Failed to load form data');
      }
    }
    loadData();
  }, [user]);

  const canSubmit = truckId && maintenanceTypeId && dateCompleted && odometer && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        truck_id: Number(truckId),
        maintenance_type_id: Number(maintenanceTypeId),
        date_completed: dateCompleted,
        odometer: Number(odometer),
        vendor_or_shop: vendorOrShop || null,
        cost: cost ? Number(cost) : null,
        notes: notes || null,
      };

      const result = await api.request('/driver-maintenance', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      // Upload receipt photo if provided
      if (receiptPhoto && result && (result.id || result.work_order_id)) {
        const woId = result.id || result.work_order_id;
        const fd = new FormData();
        fd.append('photo', receiptPhoto);
        try {
          await fetch(`/api/driver-maintenance/${woId}/photo`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: fd,
          });
        } catch {
          // Photo upload failure is non-critical
        }
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to submit maintenance record');
    }
    setSubmitting(false);
  }

  // --- SUCCESS SCREEN ---
  if (success) {
    return (
      <div className="inspection-form">
        <div className="inspection-form__complete">
          <div className="inspection-form__complete-icon">&#10003;</div>
          <h2>Maintenance Logged Successfully</h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
            Your maintenance record has been submitted.
          </p>
          <button
            className="btn btn-gold"
            onClick={() => navigate('/maintenance')}
            style={{ marginTop: '20px' }}
          >
            Back to Maintenance
          </button>
        </div>
      </div>
    );
  }

  // --- FORM ---
  return (
    <div className="inspection-form">
      <div className="inspection-form__header">
        <h1>Log Maintenance</h1>
        <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0' }}>
          Submit a maintenance record for work you've completed
        </p>
        {user && <p style={{ margin: '4px 0 0', opacity: 0.7 }}>Driver: {user.name}</p>}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Truck */}
        <div className="inspection-form__section">
          <div className="form-group">
            <label>Truck / Unit Number *</label>
            <select
              value={truckId}
              onChange={e => setTruckId(e.target.value)}
              required
              className="form-input"
            >
              <option value="">Select Truck</option>
              {trucks.filter(t => t.status === 'active').map(t => (
                <option key={t.id} value={t.id}>
                  {t.unit_number} — {[t.make, t.model].filter(Boolean).join(' ')}
                </option>
              ))}
            </select>
          </div>

          {/* Maintenance Type */}
          <div className="form-group">
            <label>Maintenance Type *</label>
            <select
              value={maintenanceTypeId}
              onChange={e => setMaintenanceTypeId(e.target.value)}
              required
              className="form-input"
            >
              <option value="">Select Type</option>
              {maintenanceTypes.map(mt => (
                <option key={mt.id} value={mt.id}>
                  {mt.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date + Odometer row */}
          <div className="form-grid">
            <div className="form-group">
              <label>Date Completed *</label>
              <input
                type="date"
                value={dateCompleted}
                onChange={e => setDateCompleted(e.target.value)}
                required
                className="form-input"
                max={getTodayCT()}
              />
            </div>
            <div className="form-group">
              <label>Odometer at Completion *</label>
              <input
                type="number"
                value={odometer}
                onChange={e => setOdometer(e.target.value)}
                required
                min="0"
                className="form-input"
                placeholder="Current mileage"
              />
            </div>
          </div>

          {/* Shop / Location */}
          <div className="form-group">
            <label>Shop / Location</label>
            <input
              type="text"
              value={vendorOrShop}
              onChange={e => setVendorOrShop(e.target.value)}
              className="form-input"
              placeholder='e.g., "Jiffy Lube on Main St" or "Done myself"'
            />
          </div>

          {/* Cost */}
          <div className="form-group">
            <label>Cost</label>
            <input
              type="number"
              value={cost}
              onChange={e => setCost(e.target.value)}
              className="form-input"
              placeholder="$0.00"
              min="0"
              step="0.01"
            />
          </div>

          {/* Notes */}
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="form-input"
              placeholder='e.g., "Changed oil and filter, added 6 quarts Mobil 1 5W-30"'
              rows={4}
            />
          </div>

          {/* Receipt Photo */}
          <div className="form-group">
            <label>Receipt Photo</label>
            <div className="inspection-form__photo-upload">
              <label className="btn btn-sm btn-outline">
                {receiptPhoto ? 'Change Photo' : 'Add Receipt Photo'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={e => setReceiptPhoto(e.target.files[0] || null)}
                />
              </label>
              {receiptPhoto && (
                <span className="inspection-form__photo-count">
                  {receiptPhoto.name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="inspection-form__actions">
          <button type="button" className="btn btn-outline" onClick={() => navigate('/maintenance')}>
            Cancel
          </button>
          <button type="submit" className="btn btn-gold btn-lg" disabled={!canSubmit}>
            {submitting ? 'Submitting...' : 'Submit Maintenance'}
          </button>
        </div>

        {/* Validation hints */}
        {!canSubmit && !submitting && (
          <p className="inspection-form__hint">
            {!truckId && 'Select a truck. '}
            {!maintenanceTypeId && 'Select a maintenance type. '}
            {!dateCompleted && 'Enter date completed. '}
            {!odometer && 'Enter odometer reading. '}
          </p>
        )}
      </form>
    </div>
  );
}
